import React, { memo, useCallback, useMemo, useState } from 'react';
import './StudentRecognitionReviewList.css';

const EMPTY_ARRAY = [];
const defaultGetName = student => student?.name || student?.student_name || '';
const defaultGetContent = student => student?.existing_content || student?.content || student?.answer_text || '';
const defaultGetMetaItems = () => EMPTY_ARRAY;
const defaultGetColumns = () => EMPTY_ARRAY;
const defaultGetKey = (student, index) => (
  student?.id
  || student?.student_id
  || student?.split_student_id
  || student?.client_student_id
  || `student-${index}`
);

const StudentRecognitionReviewCard = memo(({
  student,
  index,
  name,
  content,
  metaItems,
  columns,
  itemKey,
  isCollapsed,
  nameLabel,
  contentLabel,
  contentPlaceholder,
  rows,
  onNameChange,
  onContentChange,
  onRemove,
  onMergePrevious,
  onToggle,
}) => (
  <article
    className={`recognition-review-card ${isCollapsed ? 'is-collapsed' : ''}`}
    role="listitem"
  >
    <div className="recognition-review-card-head">
      <div className="recognition-review-card-identity">
        <span className="recognition-review-number" aria-hidden="true">{index + 1}</span>
        <div>
          <strong>{name || `학생 ${index + 1}`}</strong>
          <span>
            {[...metaItems, `${String(content || '').trim().length.toLocaleString()}자`]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      </div>

      <div className="recognition-review-card-actions">
        {onMergePrevious && index > 0 && (
          <button
            type="button"
            className="recognition-review-merge"
            onClick={() => onMergePrevious(index, student)}
            aria-label={`${name || `학생 ${index + 1}`} 칸은 위와 같은 학생`}
          >
            위와 같은 학생
          </button>
        )}
        {onRemove && (
          <button
            type="button"
            className="recognition-review-remove"
            onClick={() => onRemove(index, student)}
            aria-label={`${name || `학생 ${index + 1}`} 목록에서 제외`}
          >
            제외
          </button>
        )}
        <button
          type="button"
          className="recognition-review-toggle"
          onClick={() => onToggle(itemKey)}
          aria-expanded={!isCollapsed}
          aria-label={`${name || `학생 ${index + 1}`} 내용 ${isCollapsed ? '펼치기' : '접기'}`}
        >
          <span>{isCollapsed ? '펼치기' : '접기'}</span>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points={isCollapsed ? '6 9 12 15 18 9' : '18 15 12 9 6 15'} />
          </svg>
        </button>
      </div>
    </div>

    {!isCollapsed && (
      <div className="recognition-review-card-content">
        {columns.length > 0 && (
          <div className="recognition-review-columns" aria-label="인식한 항목">
            {columns.slice(0, 8).map((column, columnIndex) => (
              <span key={`${column}-${columnIndex}`}>{column}</span>
            ))}
          </div>
        )}

        <div className="recognition-review-fields">
          <label>
            <span>{nameLabel}</span>
            <input
              value={name}
              onChange={event => onNameChange(index, event.target.value)}
              placeholder="학생 이름"
              aria-label={`${index + 1}번 ${nameLabel}`}
            />
          </label>

          <label>
            <span>{contentLabel}</span>
            <textarea
              value={content}
              onChange={event => onContentChange(index, event.target.value)}
              placeholder={contentPlaceholder}
              rows={rows}
              aria-label={`${name || `${index + 1}번 학생`} ${contentLabel}`}
            />
          </label>
        </div>
      </div>
    )}
  </article>
), (previous, next) => (
  previous.student === next.student
  && previous.index === next.index
  && previous.name === next.name
  && previous.content === next.content
  && previous.metaItems.join('\u0000') === next.metaItems.join('\u0000')
  && previous.columns.join('\u0000') === next.columns.join('\u0000')
  && previous.nameLabel === next.nameLabel
  && previous.contentLabel === next.contentLabel
  && previous.contentPlaceholder === next.contentPlaceholder
  && previous.rows === next.rows
  && previous.onNameChange === next.onNameChange
  && previous.onContentChange === next.onContentChange
  && previous.onRemove === next.onRemove
  && previous.onMergePrevious === next.onMergePrevious
  && previous.onToggle === next.onToggle
  && previous.itemKey === next.itemKey
  && previous.isCollapsed === next.isCollapsed
));

StudentRecognitionReviewCard.displayName = 'StudentRecognitionReviewCard';

const StudentRecognitionReviewList = ({
  students = EMPTY_ARRAY,
  getName = defaultGetName,
  getContent = defaultGetContent,
  getMetaItems = defaultGetMetaItems,
  getColumns = defaultGetColumns,
  getKey = defaultGetKey,
  onNameChange,
  onContentChange,
  onRemove,
  onMergePrevious,
  nameLabel = '학생 이름',
  contentLabel = 'AI가 참고할 내용',
  contentPlaceholder = '학생 자료에서 읽은 내용을 확인하고 필요하면 바로 수정해주세요.',
  rows = 4,
  ariaLabel = '학생별 인식 내용 확인',
}) => {
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const normalizedStudents = Array.isArray(students) ? students : EMPTY_ARRAY;
  const reviewItems = useMemo(() => (
    normalizedStudents.map((student, index) => {
      const content = String(getContent(student, index) || '');
      const name = String(getName(student, index) || '');
      return {
        key: getKey(student, index),
        student,
        index,
        name,
        content,
        metaItems: (getMetaItems(student, index) || []).filter(Boolean),
        columns: (getColumns(student, index) || []).filter(Boolean),
      };
    })
  ), [
    getColumns,
    getContent,
    getKey,
    getMetaItems,
    getName,
    normalizedStudents,
  ]);
  const handleToggle = useCallback((key) => {
    setCollapsedKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  if (reviewItems.length === 0) return null;

  return (
    <section className="recognition-review" aria-label={ariaLabel}>
      <div className="recognition-review-list" role="list">
        {reviewItems.map((item) => {
          const { key, ...cardProps } = item;
          return (
            <StudentRecognitionReviewCard
              key={key}
              {...cardProps}
              itemKey={key}
              isCollapsed={collapsedKeys.has(key)}
              nameLabel={nameLabel}
              contentLabel={contentLabel}
              contentPlaceholder={contentPlaceholder}
              rows={rows}
              onNameChange={onNameChange}
              onContentChange={onContentChange}
              onRemove={onRemove}
              onMergePrevious={onMergePrevious}
              onToggle={handleToggle}
            />
          );
        })}
      </div>
    </section>
  );
};

export default StudentRecognitionReviewList;
